import { useState } from "react";
import { Link } from "react-router-dom";
import { Input } from "@hyperhedral/ui";
import { useAuthStore } from "../stores/auth";
import { supabase } from "../lib/supabase";

const NOT_ALLOWLISTED_MESSAGE =
  "This email isn't on the Bookburrow private beta allowlist yet. If you believe this is a mistake, contact us at support@bookburrow.uk.";

export function Signup() {
  const [fullName, setFullName] = useState("");
  const [schoolName, setSchoolName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const { signUp, loading } = useAuthStore();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    const { data: allowed, error: checkError } = await supabase.rpc(
      "check_signup_allowlist",
      { p_email: email },
    );
    if (checkError) {
      setError("Could not verify your invite. Please try again in a moment.");
      return;
    }
    if (!allowed) {
      setError(NOT_ALLOWLISTED_MESSAGE);
      return;
    }

    const { error } = await signUp(email, password, schoolName, fullName);
    if (error) {
      if (/signup_not_allowlisted|not.*allowlist/i.test(error)) {
        setError(NOT_ALLOWLISTED_MESSAGE);
      } else {
        setError(error);
      }
    } else {
      setSuccess(true);
    }
  }

  return (
    <div className="account-page flex min-h-[calc(100vh-5rem)] items-center justify-center px-6 py-16">
      <div className="account-card w-full max-w-md">
        <div className="text-center">
          <img
            src="/android-chrome-192x192.png"
            alt="Bookburrow"
            className="mx-auto h-14 w-14 rounded-2xl shadow-lg"
          />
          <h1 className="mt-6 font-display text-2xl font-semibold text-forest-950">
            Start your free trial
          </h1>
          <p className="mt-2 text-sm text-bark-600">
            30 days free. No credit card required.
          </p>
        </div>

        <div className="mt-6 rounded-lg border border-gold-300/60 bg-gold-50 px-4 py-3 text-sm text-bark-800">
          <p className="font-semibold text-bark-900">
            Bookburrow is currently invite-only.
          </p>
          <p className="mt-1">
            We&rsquo;re in a private beta with a small group of schools. If
            you&rsquo;ve been invited, use the email address we sent the
            invite to. Otherwise,{" "}
            <a
              href="/#waitlist"
              className="font-medium text-forest-700 underline decoration-forest-300 underline-offset-2"
            >
              join the waitlist
            </a>{" "}
            and we&rsquo;ll be in touch when we open up.
          </p>
        </div>

        {error && (
          <div className="mt-6 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {success ? (
          <div className="mt-10 space-y-4">
            <div className="rounded-lg bg-forest-50 px-4 py-4 text-sm text-forest-700">
              <p className="font-semibold">Check your email</p>
              <p className="mt-1">
                We've sent a verification link to{" "}
                <span className="font-medium">{email}</span>. Click the link to
                activate your account.
              </p>
            </div>
            <Link
              to="/portal/login"
              className="block w-full rounded-xl bg-forest-800 px-4 py-3 text-center text-sm font-semibold text-cream-50 transition-colors hover:bg-forest-700"
            >
              Go to sign in
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-10 space-y-5">
            <Input
              label="Your name"
              placeholder="e.g. Julia Smith"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
            />
            <Input
              label="School name"
              placeholder="e.g. Oakwood Primary School"
              value={schoolName}
              onChange={(e) => setSchoolName(e.target.value)}
              required
            />
            <Input
              label="Email address"
              type="email"
              placeholder="you@school.sch.uk"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <Input
              label="Password"
              type="password"
              placeholder="At least 8 characters"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />

            <label className="flex items-start gap-3 text-sm text-bark-700">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                required
                className="mt-1 accent-forest-700"
              />
              <span>
                I agree to the <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-forest-700 underline">Terms and conditions</Link>
                {" "}and have read the <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-forest-700 underline">Privacy Policy</Link>.
              </span>
            </label>

            <button
              type="submit"
              disabled={loading || !acceptedTerms}
              className="primary-action mt-2 w-full rounded-xl bg-gold-500 px-4 py-3 text-sm font-bold text-bark-900 shadow-lg shadow-gold-500/20 transition-all hover:bg-gold-400 hover:shadow-xl disabled:opacity-60"
            >
              {loading ? "Creating account..." : "Create account"}
            </button>
          </form>
        )}

        <p className="mt-8 text-center text-xs text-bark-600/60">
          Already have an account?{" "}
          <Link
            to="/portal/login"
            className="font-medium text-forest-600 underline decoration-forest-300 underline-offset-2"
          >
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
